import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api/api';
import { API_ENDPOINTS } from '../config/api';
import { useAuth } from '../context/AuthContext';
import SessionExpiryModal from './SessionExpiryModal';

type SessionInfoResponse = {
  accessTokenExpiresAtMs: number;
  nowMs: number;
};

export default function SessionExpiryManager() {
  const { isAuthenticated, logout } = useAuth();
  const navigate = useNavigate();

  const [open, setOpen] = useState(false);
  const [expiresAtMs, setExpiresAtMs] = useState(0);
  const [timeLeftSec, setTimeLeftSec] = useState(0);
  const [loading, setLoading] = useState(false);
  const clockSkewMsRef = useRef<number>(0);
  const timerRef = useRef<number | null>(null);
  const pollRef = useRef<number | null>(null);
  const countdownRef = useRef<number | null>(null);
  const autoLogoutRef = useRef<number | null>(null);
  const warningWindowSecRef = useRef<number>(5 * 60);

  const clearTimers = () => {
    if (timerRef.current != null) {
      window.clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    if (pollRef.current != null) {
      window.clearInterval(pollRef.current);
      pollRef.current = null;
    }

    if (countdownRef.current != null) {
      window.clearInterval(countdownRef.current);
      countdownRef.current = null;
    }
    if (autoLogoutRef.current != null) {
      window.clearTimeout(autoLogoutRef.current);
      autoLogoutRef.current = null;
    }
  };

  const doLogout = useCallback(async () => {
    try {
      await api.post(API_ENDPOINTS.logout);
    } catch {
      // ignore
    }
    logout();
    navigate('/');
  }, [logout, navigate]);

  const schedule = useCallback((newExpiresAtMs: number) => {
    setExpiresAtMs(newExpiresAtMs);

    const alignedNowMs = Date.now() - clockSkewMsRef.current;
    const initialLeftSec = Math.max(0, Math.floor((newExpiresAtMs - alignedNowMs) / 1000));
    warningWindowSecRef.current = Math.min(5 * 60, initialLeftSec);

    const showAtMs = newExpiresAtMs - 5 * 60 * 1000;
    const delay = Math.max(0, showAtMs - alignedNowMs);

    if (timerRef.current != null) {
      window.clearTimeout(timerRef.current);
    }

    timerRef.current = window.setTimeout(() => {
      setOpen(true);
    }, delay);
  }, []);

  const fetchSessionInfo = useCallback(async () => {
    if (!isAuthenticated) return;

    try {
      const res = await api.get<SessionInfoResponse>(API_ENDPOINTS.session);
      if (typeof res.data?.accessTokenExpiresAtMs === 'number') {
        if (typeof res.data?.nowMs === 'number') {
          clockSkewMsRef.current = Date.now() - res.data.nowMs;
        }
        schedule(res.data.accessTokenExpiresAtMs);
      }
    } catch (e: any) {
      if (e?.response?.status === 401) {
        clearTimers();
        setOpen(false);
        setTimeLeftSec(0);
        logout();
        navigate('/');
      }
    }
  }, [isAuthenticated, logout, navigate, schedule]);

  const onContinue = useCallback(async () => {
    setLoading(true);
    try {
      await api.post(API_ENDPOINTS.refresh);
      setOpen(false);
      await fetchSessionInfo();
    } catch {
      await doLogout();
    } finally {
      setLoading(false);
    }
  }, [doLogout, fetchSessionInfo]);

  const onLogoutNow = useCallback(async () => {
    setOpen(false);
    await doLogout();
  }, [doLogout]);

  useEffect(() => {
    clearTimers();
    setOpen(false);
    setTimeLeftSec(0);

    if (!isAuthenticated) {
      return;
    }

    fetchSessionInfo();

    pollRef.current = window.setInterval(() => {
      fetchSessionInfo();
    }, 60 * 1000);

    const onVis = () => {
      if (document.visibilityState === 'visible') {
        fetchSessionInfo();
      }
    };
    document.addEventListener('visibilitychange', onVis);

    return () => {
      document.removeEventListener('visibilitychange', onVis);
      clearTimers();
    };
  }, [fetchSessionInfo, isAuthenticated]);

  useEffect(() => {
    if (!isAuthenticated || !open || !expiresAtMs) return;

    const update = () => {
      const alignedNowMs = Date.now() - clockSkewMsRef.current;
      const left = Math.max(0, Math.floor((expiresAtMs - alignedNowMs) / 1000));
      setTimeLeftSec(left);
      return left;
    };

    const initial = update();
    if (countdownRef.current != null) {
      window.clearInterval(countdownRef.current);
    }
    countdownRef.current = window.setInterval(() => {
      update();
    }, 1000);

    if (autoLogoutRef.current != null) {
      window.clearTimeout(autoLogoutRef.current);
    }

    const alignedNowMs = Date.now() - clockSkewMsRef.current;
    autoLogoutRef.current = window.setTimeout(() => {
      doLogout();
    }, Math.max(0, expiresAtMs - alignedNowMs));

    // If already expired at open time, logout immediately
    if (initial <= 0) {
      doLogout();
    }

    return () => {
      if (countdownRef.current != null) {
        window.clearInterval(countdownRef.current);
        countdownRef.current = null;
      }
      if (autoLogoutRef.current != null) {
        window.clearTimeout(autoLogoutRef.current);
        autoLogoutRef.current = null;
      }
    };
  }, [doLogout, expiresAtMs, isAuthenticated, open]);

  return (
    <SessionExpiryModal
      open={open && isAuthenticated}
      expiresAtMs={expiresAtMs}
      onContinue={onContinue}
      onLogoutNow={onLogoutNow}
      loading={loading}
      timeLeftSec={timeLeftSec}
      warningWindowSec={warningWindowSecRef.current}
    />
  );
}
